import { StateCreator } from 'zustand'
import { SharedSlice } from '../../../../slice'
import {
  Calendar,
  EarningsRate,
  ICompanyDefaultWorkingHours,
  PayClassification,
  IStaffMemberDetail,
  StaffRole as IStaffRole,
  SuperFund,
  IPosStaffMember,
} from '$models'
import { exceptionMessageHandler } from '$utils'
import {
  AuthModule,
  PayrollSystem,
  ThirdPartyConnectionStatus,
} from '$constants'
import {
  staffMemberService,
  payrollService,
  payClassificationService,
  staffRoleService,
  settingsService,
} from '$services'
import sortBy from 'lodash/sortBy'
import { getStaffMemberAccess } from './_common/access'

interface StaffMembersState {
  activeStaffMembers: IStaffMemberDetail[]
  deactivatedStaffMembers: IStaffMemberDetail[]
  showDeactivated: boolean
  posStaffMembers: IPosStaffMember[]
  staffRoles: IStaffRole[]
  payClassifications: PayClassification[]
  calendars: Calendar[]
  earningsRates: EarningsRate[]
  superFunds: SuperFund[]
  companyDefaultWorkingHours: ICompanyDefaultWorkingHours[]
}
export interface StaffMembersSlice extends StaffMembersState {
  setShowDeactivated: () => void

  /**
   * Get Staff Members list
   * @param includeDeleted denotes if the deleted staff members should be included
   * @param includeLastClocks denotes if the last clock per staff member should be included
   */
  getStaffMembers: (
    includeDeleted?: boolean,
    includeLastClocks?: boolean
  ) => Promise<void>

  /**
   * Get Staff Roles
   * @param includeDeleted
   */
  getStaffRoles: (includeDeleted: boolean) => Promise<void>

  /**
   * Get POS Staff Members
   * @param includeDeleted
   */
  getPosStaff: (includeDeleted: boolean) => Promise<void>

  /**
   * Get Pay Classifications list
   */
  getPayClassifications: () => Promise<void>

  /**
   * Get Pay Calendars list
   * @param payrollSystem company payroll system
   */
  getCalendars: (
    payrollSystem: PayrollSystem,
    venueId?: string | null
  ) => Promise<void>

  /**
   * Get Payroll Ordinary Earnings Rates list
   * @param payrollSystem company payroll system
   */
  getEarningsRates: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Get SuperFunds list
   */
  getSuperFunds: () => Promise<void>

  /**
   * Check Third Party Connection Status
   * @param authModule AuthModule to check
   */
  checkThirdPartyConnectionStatus: (authModule: AuthModule) => Promise<boolean>

  /**
   * Get all company default working hours
   * @returns Promise<void>
   */
  getCompanyDefaultWorkingHours: () => Promise<void>

  /**
   * Resets StaffMembers Slice state
   */
  resetStaffMembersSlice: () => void
}

const initialState: StaffMembersState = {
  activeStaffMembers: [],
  deactivatedStaffMembers: [],
  posStaffMembers: [],
  showDeactivated: false,
  staffRoles: [],
  payClassifications: [],
  calendars: [],
  earningsRates: [],
  superFunds: [],
  companyDefaultWorkingHours: [],
}

const createStaffMembersSlice: StateCreator<
  StaffMembersSlice & SharedSlice,
  [],
  [],
  StaffMembersSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),
  resetStaffMembersSlice: () => set(initialState),

  // State API calls
  getPosStaff: async (includeDeleted: boolean): Promise<void> => {
    return staffMemberService
      .getPosStaffMembers(includeDeleted)
      .then((result) =>
        set(() => ({
          posStaffMembers: sortBy<IPosStaffMember>(result, 'posName'),
        }))
      )
      .catch((error) => {
        set(() => ({
          posStaffMembers: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve Linked POS Staff Members'
        )
      })
  },

  getStaffRoles: async (includeDeleted: boolean): Promise<void> => {
    return staffRoleService
      .getAll(includeDeleted)
      .then((result) => set(() => ({ staffRoles: result })))
      .catch((error) => {
        set(() => ({
          staffRoles: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve Linked POS Staff Roles'
        )
      })
  },

  getStaffMembers: async (
    includeDeleted: boolean = false,
    includeLastClocks: boolean = false
  ): Promise<void> => {
    set(() => ({ isLoading: true }))
    //It defaults to today's date in order to get all the staff members until today
    return staffMemberService
      .getAll(undefined, includeDeleted, includeLastClocks)
      .then((result) => {
        if (!includeDeleted) {
          return set(() => ({ activeStaffMembers: result }))
        } else {
          const deactivatedMembers = result.filter(
            (member) => member.datestampDeleted != null
          )
          return set(() => ({ deactivatedStaffMembers: deactivatedMembers }))
        }
      })
      .catch((error) => {
        if (!includeDeleted) {
          set(() => ({
            activeStaffMembers: [],
          }))
        } else {
          set(() => ({
            deactivatedStaffMembers: [],
          }))
        }
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  getPayClassifications: async (): Promise<void> => {
    if (!getStaffMemberAccess().canViewPayRates()) {
      return set(() => ({ payClassifications: [] }))
    }

    return payClassificationService
      .getAll()
      .then((result) =>
        set({
          payClassifications: result,
        })
      )
      .catch((error) => {
        set(() => ({
          payClassifications: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve pay classifications')
      })
  },

  getCalendars: async (
    payrollSystem: PayrollSystem,
    venueId?: string | null
  ): Promise<void> => {
    return payrollService
      .getCalendars(payrollSystem, venueId)
      .then((result) => {
        set(() => ({
          calendars: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          calendars: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve third party calendars'
        )
      })
  },

  getEarningsRates: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getEarningsRates(payrollSystem)
      .then((result) => {
        set(() => ({
          earningsRates: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          earningsRates: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve third party earnings rates'
        )
      })
  },

  getSuperFunds: async (): Promise<void> => {
    return payrollService
      .getSuperFunds()
      .then((result) => {
        set(() => ({
          superFunds: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          superFunds: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve super funds')
      })
  },

  checkThirdPartyConnectionStatus: async (
    authModule: AuthModule
  ): Promise<boolean> => {
    return payrollService
      .getThirdPartyConnectionStatus(authModule)
      .then((result) => {
        return result.connectionStatus === ThirdPartyConnectionStatus.Connected
      })
      .catch((error) => {
        exceptionMessageHandler(
          error,
          'Failed to check third party connection status'
        )
        return false
      })
  },

  getCompanyDefaultWorkingHours: async (): Promise<void> => {
    return settingsService
      .getCompanyDefaultWorkingHours()
      .then((result) =>
        set(() => ({
          companyDefaultWorkingHours: result,
        }))
      )
      .catch((error) => {
        set(() => ({
          companyDefaultWorkingHours: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to get the company default working hours'
        )
      })
  },
})

export default createStaffMembersSlice
